import { ChangeDetectorRef, Component, OnDestroy, OnInit, AfterViewInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthGuard } from '@app-core/guards/auth/auth.guard';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
import { AsyncDownloadsService } from '@app-core/services/async-downloads/async-downloads.service';
import { AsyncReportType } from '@app-core/constants/constants';
import { MatDialog } from '@angular/material/dialog';
import { DownloadReportComponent } from '@app-shared/components/download-report/download-report.component';

const tabNameIndexMapping = {
  overview: 0,
  'export-history': 1,
  scheduled: 2,
};
@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy, AfterViewInit {
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public currentOS = 'windows';
  public currentTabIndex = 0;
  public fleetId: string;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authGuard: AuthGuard,
    public dataService: DataService,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private gtmService: GoogleTagManagerService,
    private cdr: ChangeDetectorRef,
    private asyncDownloadsService: AsyncDownloadsService,
    private dialog: MatDialog,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
      }
    });

    this.currentOS = this.dataService.getCurrentOS();
    this.configureKeyboardShortcuts();
  }

  ngAfterViewInit(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((param: any) => {
      const currentTab = param.tab;
      this.currentTabIndex = tabNameIndexMapping[currentTab] || 0;
      if (this.currentTabIndex === 0) {
        this.gtmService.customTabs('/reports', 'Reports', 'Reports Overview');
      } else if (this.currentTabIndex === 1) {
        this.gtmService.customTabs('/reports', 'Reports', 'Export History');
      } else {
        this.gtmService.customTabs('/reports', 'Reports', 'scheduled');
      }
      this.cdr.detectChanges();
    });
  }

  public onPageTabChange(event: MatTabChangeEvent) {
    let tabName = event.tab.textLabel.toString() || 'overview';
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
    });
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Overview Tab clicked - Reports Page';
    } else if (tabName === 'export-history') {
      displayTabName = 'Export History Tab Clicked - Reports Page';
    } else {
      displayTabName = 'Scheduled Tab Clicked - Reports Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
    if (!this.authGuard.isRouteAccessible('reports')) {
      this.router.navigate(['home']); // Redirect to home if not accessible
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public navigateTo(route: string) {
    this.router.navigate([route], { relativeTo: this.route });
    this.gtmService.gotoFleetSafetyReportFromReportPage(this.fleetId);
    this.mixpanelService.track('Navigating to Fleet Safety Report from Reports Page', {
      'Fleet Id': this.fleetId,
    });
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab0[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(0);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab1[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(1);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab2[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(2);
      });
  }
  public scheduleFleetSafety() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab - Fleet Safety Report', {
      'Report Type': AsyncReportType.fleetSafety.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.fleetSafety.key);
  }

  public scheduleCoachingSession() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab -Coaching Session Report', {
      'Report Type': AsyncReportType.coachingSession.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.coachingSession.key);
  }

  public schedulePrivacyMode() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab -Privacy Mode Report', {
      'Report Type': AsyncReportType.privacyModeEventsReport.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.privacyModeEventsReport.key);
  }
  public scheduleEventListReport() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab -Event List Report', {
      'Report Type': AsyncReportType.eventListReport.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.eventListReport.key);
  }
  public scheduleEventCountReport() {
    this.mixpanelService.track('Schedule Clicked- From Overview Tab -Event Count Report', {
      'Report Type': AsyncReportType.eventCountReport.key,
    });
    this.asyncDownloadsService.navigateToCreateSchedule(AsyncReportType.eventCountReport.key);
  }

  public openDialog(component: any, reportType: string) {
    this.dialog.open(component, {
      width: '600px',
      minHeight: '300px',
      data: { reportType: reportType },
    });
  }
  public openReportDialog(reportType: string) {
    this.openDialog(DownloadReportComponent, reportType);
  }

  public onKeyboardTabChange(tabIndex: number) {
    const [tabName = 'overview'] = Object.entries(tabNameIndexMapping).find(([, idx]) => idx === tabIndex);
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
    });
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Overview Tab clicked - Reports Page';
    } else if (tabName === 'export-history') {
      displayTabName = 'Export History Tab Clicked - Reports Page';
    } else {
      displayTabName = 'Scheduled Tab Clicked - Reports Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
  }
}
